import { GridApi } from '@ag-grid-community/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  linkedSignal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import dayjs from 'dayjs';
import { firstValueFrom } from 'rxjs';

import { ExchangeLinkComponent } from '@features/exchange-link/exchange-link.component';
import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';

import { ButtonComponent } from '@shared/components/button/button.component';
import { TablePanelComponent } from '@shared/components/table-panel/table-panel.components';
import { TableSkeletonComponent } from '@shared/components/table-skeleton/table-skeleton.component';
import { ToggleComponent } from '@shared/components/toggle/toggle.component';
import { LocalStorageKey } from '@shared/constants/localStorageKey';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { EntityType, EventType } from '@shared/services/gql.service';
import { injectFlags } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { QuarterCloseAdjustmentsService } from '@shared/services/quarter-close-adjustments.service';
import { QuarterCloseChecklistPeriodCloseService } from '@shared/services/quarter-close-checklist-period-close.service';
import { injectPermissions } from '@shared/store/auth/auth.service';
import { EventService } from '@shared/store/event/event.service';
import { GlobalStore } from '@shared/store/global/global.store';
import { InvoiceService } from '@shared/store/invoice/invoice.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { CurrencyToggle } from '@shared/types/components.type';
import { ExportType } from '@shared/utils/utils';

import { PeriodCloseComponent } from '../../period-close.component';
import { AddVendorEstimateUploadComponent } from './add-vendor-estimate-upload/add-vendor-estimate-upload.component';
import { DownloadVendorEstimateComponent } from './download-vendor-estimate.component';
import { QcTableComponent } from './qc-table/qc-table.component';
import {
  QuarterCloseBannerComponent,
  QuarterCloseBannerHeight,
} from './quarter-close-banner/quarter-close-banner.component';
import { QuarterCloseService } from './quarter-close.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    // todo(refactor) check if this padding is working
    'style.padding-bottom.px': `${QuarterCloseBannerHeight}`,
  },
  imports: [
    ExportExcelButtonComponent,
    QuarterCloseBannerComponent,
    TablePanelComponent,
    TableSkeletonComponent,
    ButtonComponent,
    DownloadVendorEstimateComponent,
    QcTableComponent,
    ToggleComponent,
    ReactiveFormsModule,
    FormsModule,
    ExchangeLinkComponent,
    TooltipDirective,
  ],
  selector: 'aux-quarter-close',
  templateUrl: './quarter-close.component.html',
})
export class QuarterCloseComponent {
  changeDetectorRef = inject(ChangeDetectorRef);
  destroyRef = inject(DestroyRef);
  eventService = inject(EventService);
  globalStore = inject(GlobalStore);
  invoiceService = inject(InvoiceService);
  mainQuery = inject(MainQuery);
  organizationQuery = inject(OrganizationQuery);
  overlayService = inject(OverlayService);
  periodCloseComponent = inject(PeriodCloseComponent);
  periodCloseService = inject(QuarterCloseChecklistPeriodCloseService);
  quarterCloseAdjustmentsService = inject(QuarterCloseAdjustmentsService);
  quarterCloseService = inject(QuarterCloseService);
  router = inject(Router);
  workflowQuery = inject(WorkflowQuery);

  flags = injectFlags({
    fluxAnalysisToggle: 'flux_analysis_toggle',
  });
  permissions = injectPermissions({
    closeMonth: 'PERMISSION_APPROVE_IN_MONTH',
  });

  isCloseMonthAvailable = computed(() => {
    const canClose = this.workflowQuery.getMonthCloseButtonAvailability();
    const isClosed = this.periodCloseComponent.monthClosed();

    return !isClosed && canClose;
  });

  fluxAnalysisMode = linkedSignal<boolean>(() => {
    const isFluxAnalysisAvailable = this.quarterCloseService.isFluxAnalysisAvailable();

    return isFluxAnalysisAvailable
      ? localStorage.getItem(LocalStorageKey.FLUX_ANALYSIS_MODE) === 'true'
      : false;
  });

  isCurrencyToggleDisabled = computed(() => {
    const fluxMode = this.fluxAnalysisMode();

    if (fluxMode) {
      return 'Flux toggle supported in Vendor Contract currency only.';
    }

    return '';
  });
  isFluxDisabled = computed(() => {
    const isUSD = this.periodCloseComponent.currencyToggle() === CurrencyToggle.PRIMARY;

    if (isUSD) {
      return 'Flux toggle supported in Vendor Contract currency only.';
    }

    return '';
  });

  trialAttributesLoading = this.globalStore.getLoading('TrialAttributes');

  loading = computed(
    () =>
      this.trialAttributesLoading() ||
      this.periodCloseComponent.newQCGridDataLoading() ||
      this.organizationQuery.loading()
  );
  showBanner = computed(() => {
    return (
      this.periodCloseComponent.isCurrentQuarterSelected() &&
      !this.periodCloseComponent.isCloseMonthsProcessing()
    );
  });
  stickyServiceOffset = computed(() => (this.showBanner() ? QuarterCloseBannerHeight : 0));

  downloadVEButton = viewChild(DownloadVendorEstimateComponent);
  tableComponent = viewChild(QcTableComponent);

  clearFilters() {
    this.tableComponent()?.gridAPI()?.setFilterModel({});
  }

  constructor() {
    this.periodCloseComponent.selectedMonth$.next(null);
    this.periodCloseService.selectedQuarterMonthChanged$.next(null);
    this.invoiceService.initialize().pipe(takeUntilDestroyed()).subscribe();
    this.globalStore.refreshRequest('TrialAttributes');
    localStorage.setItem(LocalStorageKey.CLOSE_SUMMARY_OPEN_MONTH_GROUP, 'true');

    effect(() => {
      const isCurrentQuarterSelected = this.periodCloseComponent.isCurrentQuarterSelected();
      const fluxAnalysisToggle = this.flags().fluxAnalysisToggle;

      this.quarterCloseService.isCurrentQuarterSelected.set(isCurrentQuarterSelected);

      if (!isCurrentQuarterSelected || !fluxAnalysisToggle) {
        this.toggleFluxAnalysisMode(false);
      }
    });
  }

  onAddVendorEstimateUploadClick = async () => {
    const currentMonth = this.mainQuery.getValue().currentOpenMonth;
    const ref = this.overlayService.openPopup<
      undefined,
      { goToEditModeWithCurrentFilters?: boolean; month?: string; vendorId?: string },
      AddVendorEstimateUploadComponent
    >({
      content: AddVendorEstimateUploadComponent,
      settings: {
        header: 'Send Vendor Estimate Support to Auxilius',
        primaryButton: {
          action: (instance) => instance?.onUpload(),
          label: 'Send Vendor Estimate Support',
        },
      },
    });

    const resp = await firstValueFrom(ref.afterClosed$);

    this.downloadVEButton()?.fileCount.refresh();

    const { goToEditModeWithCurrentFilters, month, vendorId } = resp.data || {};

    if (vendorId || goToEditModeWithCurrentFilters) {
      if (vendorId) {
        this.quarterCloseAdjustmentsService.updateFormControlValues('', [vendorId]);
      }

      const filterMonth = goToEditModeWithCurrentFilters ? currentMonth : month;

      this.periodCloseComponent.selectedQuarter.setValue(
        this.periodCloseComponent.currentQuarter()
      );

      const editQueryParam =
        dayjs(month).format('YYYY-MM') === dayjs(currentMonth).format('YYYY-MM')
          ? {
              editMode: true,
            }
          : {};

      void this.router.navigate(
        [`/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.EXPENSE_DETAIL}`],
        {
          queryParams: {
            ...editQueryParam,
            currentOpenMonth: dayjs(filterMonth).add(2, 'day').format('YYYY-MM-DD'),
            vendorId: vendorId ?? null,
          },
        }
      );
    }
  };

  onCurrencyChange(toggleState: `${CurrencyToggle}`) {
    this.periodCloseComponent.currencyToggle.set(toggleState as CurrencyToggle);
  }

  async onExport() {
    const trialName = this.mainQuery.getSelectedTrial()?.short_name || '';

    const { errors, success } = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: '',
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          export_type: ExportType.CLOSE_SUMMARY,
          filename: `${trialName}_${this.periodCloseComponent.selectedQuarter.value?.replace(
            '-',
            ' '
          )}_Close_Summary`,
          json_properties: {
            is_flux_analysis_toggled_on: this.fluxAnalysisMode(),
            is_trial_currency:
              this.periodCloseComponent.currencyToggle() === CurrencyToggle.PRIMARY,
            period: this.periodCloseComponent.selectedQuarter.value?.replace('-', ' '),
          },
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );

    if (success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(errors);
    }
  }

  async onRequestJournalEntryReport() {
    const result = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: '',
        entity_type: EntityType.DOCUMENT,
        payload: JSON.stringify({
          current_open_month: this.periodCloseComponent.currentMonthSignal().replace('-', ' '),
        }),
        type: EventType.REQUEST_JOURNAL_ENTRY_REPORT_NOTIFICATION,
      })
    );

    if (result.success) {
      this.overlayService.success('Request for Journal Entry Report successfully processed');
    } else {
      this.overlayService.error('Error Requesting Journal Entry Report');
    }
  }

  syncHorizontalScrollOnExternalToggle(): void {
    // on external expand open month section scroll is broken
    const bodyHorizontalScrollViewport = document.querySelector(
      '.ag-body-horizontal-scroll-viewport'
    );

    if (bodyHorizontalScrollViewport) {
      bodyHorizontalScrollViewport.scrollLeft = 0;
      bodyHorizontalScrollViewport.dispatchEvent(new Event('scroll'));
    }
  }

  toggleFluxAnalysisMode(value: boolean): void {
    this.fluxAnalysisMode.set(value);
    localStorage.setItem(LocalStorageKey.FLUX_ANALYSIS_MODE, String(value));

    this.quarterCloseService.toggleFluxAnalysisMode(
      this.tableComponent()?.gridAPI() as GridApi,
      value
    );

    if (value) {
      if (localStorage.getItem(LocalStorageKey.CLOSE_SUMMARY_QUARTER_SUMMARY_GROUP) !== 'true') {
        this.tableComponent()?.gridAPI()?.setColumnGroupOpened('summaryGroup', true);
      }

      if (localStorage.getItem(LocalStorageKey.CLOSE_SUMMARY_OPEN_MONTH_GROUP) !== 'true') {
        setTimeout(() => {
          this.quarterCloseService.callToggle(true);
          this.syncHorizontalScrollOnExternalToggle();
        });
      } else {
        this.tableComponent()
          ?.gridAPI()
          ?.ensureColumnVisible('previous_month_wp_estimate', 'start');
      }
    }
  }
}
